import './style.scss';

import {
	__experimentalUnitControl as UnitControl,
	Button,
} from '@wordpress/components';
import { useEffect, useState } from '@wordpress/element';
import { link, linkOff } from '@wordpress/icons';
import classnames from 'classnames';
import React from 'react';

import { BOX_CONTROL_LABELS, DEFAULT_SIDES, DEFAULT_UNITS } from './constants';
import { BoxControlProps, BoxSide, BoxValues } from './types';

export const BoxControl: React.FC< BoxControlProps > = ( {
	value = {},
	onChange,
	splitOnAxis = false,
	units = DEFAULT_UNITS,
	placeholder = '0',
	showAllSides = true,
	min = 0,
	max,
	step = 1,
	disabled = false,
	className = '',
	resetFallbackValue = '0',
	sides = DEFAULT_SIDES,
	isRadius = false,
} ) => {
	const [ isLinked, setIsLinked ] = useState( ! splitOnAxis );
	const [ linkedValue, setLinkedValue ] = useState< string >( '' );

	// Initialize linked value from existing values
	useEffect( () => {
		if ( isLinked && value?.top ) {
			setLinkedValue( value.top );
		}
	}, [ isLinked, value?.top ] );

	const handleSingleValueChange = ( newValue: string | undefined ) => {
		const finalValue = newValue ?? '';
		setLinkedValue( finalValue );

		// When value is empty, use resetFallbackValue
		if ( finalValue === '' || finalValue === undefined ) {
			const fallbackValue =
				resetFallbackValue.includes( 'px' ) ||
				resetFallbackValue.includes( 'em' ) ||
				resetFallbackValue.includes( 'rem' ) ||
				resetFallbackValue.includes( '%' ) ||
				resetFallbackValue.includes( 'vw' ) ||
				resetFallbackValue.includes( 'vh' )
					? resetFallbackValue
					: `${ resetFallbackValue }px`;
			const newBoxValue: BoxValues = {
				top: fallbackValue,
				right: fallbackValue,
				bottom: fallbackValue,
				left: fallbackValue,
			};
			onChange( newBoxValue );
			return;
		}

		// Format the value with units if needed
		const formattedValue =
			finalValue.includes( 'px' ) ||
			finalValue.includes( 'em' ) ||
			finalValue.includes( 'rem' ) ||
			finalValue.includes( '%' ) ||
			finalValue.includes( 'vw' ) ||
			finalValue.includes( 'vh' )
				? finalValue
				: `${ finalValue }px`;

		const newBoxValue: BoxValues = {
			top: formattedValue,
			right: formattedValue,
			bottom: formattedValue,
			left: formattedValue,
		};

		onChange( newBoxValue );
	};

	const handleIndividualValueChange = (
		side: BoxSide,
		newValue: string | undefined,
	) => {
		const finalValue = newValue ?? '';

		// When value is empty, use resetFallbackValue for this side only
		if ( finalValue === '' || finalValue === undefined ) {
			const fallbackValue =
				resetFallbackValue.includes( 'px' ) ||
				resetFallbackValue.includes( 'em' ) ||
				resetFallbackValue.includes( 'rem' ) ||
				resetFallbackValue.includes( '%' ) ||
				resetFallbackValue.includes( 'vw' ) ||
				resetFallbackValue.includes( 'vh' )
					? resetFallbackValue
					: `${ resetFallbackValue }px`;
			const newBoxValue: BoxValues = {
				...value,
				[ side ]: fallbackValue,
			};
			onChange( newBoxValue );
			return;
		}

		// Format the value with units if needed
		const formattedValue =
			finalValue.includes( 'px' ) ||
			finalValue.includes( 'em' ) ||
			finalValue.includes( 'rem' ) ||
			finalValue.includes( '%' ) ||
			finalValue.includes( 'vw' ) ||
			finalValue.includes( 'vh' )
				? finalValue
				: `${ finalValue }px`;

		const newBoxValue: BoxValues = {
			...value,
			[ side ]: formattedValue,
		};

		onChange( newBoxValue );
	};

	const toggleLinked = () => {
		const newIsLinked = ! isLinked;
		setIsLinked( newIsLinked );

		if ( newIsLinked ) {
			// When linking, use the top value as the linked value
			const topValue = value?.top ?? resetFallbackValue;
			setLinkedValue( topValue );
			handleSingleValueChange( topValue );
		}
	};

	const renderSingleControl = () => (
		<UnitControl
			value={ linkedValue }
			onChange={ handleSingleValueChange }
			units={ units }
			placeholder={ placeholder }
			min={ min }
			max={ max }
			step={ step }
			disabled={ disabled }
			__next40pxDefaultSize
		/>
	);

	const renderIndividualControls = () => {
		const sideOrder: BoxSide[] = [
			BoxSide.TOP,
			BoxSide.RIGHT,
			BoxSide.BOTTOM,
			BoxSide.LEFT,
		];

		return (
			<div className="redfox-thank-you-box-control__inputs">
				{ sideOrder.map( ( side ) => (
					<div
						key={ side }
						className={ `redfox-thank-you-box-control__input-wrapper redfox-thank-you-box-control__input-wrapper--${ side }` }
					>
						<label className="redfox-thank-you-box-control__side-label">
							{ sides[ side ] }
						</label>
						<UnitControl
							value={ value?.[ side ] ?? '' }
							onChange={ ( newValue: string | undefined ) =>
								handleIndividualValueChange( side, newValue )
							}
							units={ units }
							placeholder={ placeholder }
							min={ min }
							max={ max }
							step={ step }
							disabled={ disabled }
							__next40pxDefaultSize
						/>
					</div>
				) ) }
			</div>
		);
	};

	return (
		<div
			className={ classnames( 'redfox-thank-you-box-control', className, {
				'redfox-thank-you-box-control--linked': isLinked,
				'redfox-thank-you-box-control--split': ! isLinked,
				'redfox-thank-you-box-control--disabled': disabled,
				'redfox-thank-you-box-control--radius': isRadius,
			} ) }
		>
			<div className="redfox-thank-you-box-control__content">
				{ showAllSides && (
					<Button
						className="redfox-thank-you-box-control__link-button"
						icon={ isLinked ? link : linkOff }
						onClick={ toggleLinked }
						variant="secondary"
						size="small"
						aria-label={
							isLinked
								? BOX_CONTROL_LABELS.unlinkSides
								: BOX_CONTROL_LABELS.linkSides
						}
						disabled={ disabled }
					/>
				) }
				{ isLinked
					? renderSingleControl()
					: renderIndividualControls() }
			</div>
		</div>
	);
};
